import React, { useContext } from 'react';
import AsyncSelect from 'react-select/async';
import styled, { keyframes } from 'styled-components';
import { fadeIn } from 'react-animations';
import { FormattedMessage as Trans } from 'react-intl';

import { MapContext } from '../contexts/map';
import { SUCCEED, GEOLOC_ACTION, GEOLOC_CATEGORY } from '../utils/variables';

import Button from './Button';
import Map from './Map';
import HeadingTwo from './HeadingTwo';
import Description from './Description';

const fadeInAnimation = keyframes`${fadeIn}`;

const FadeInDiv = styled.div`
  animation: 2s ${fadeInAnimation};
`;

export default function Geolocalisation() {
  const {
    pickupLocation,
    deliveryLocation,
    setPickupLocation,
    setDeliveryLocation,
    showMessage,
    messageId,
    apiAnswer,
    isCitiesFilledOut,
    onClickSearch,
    loadOptions,
    handleSelectOption
  } = useContext(MapContext);

  const asyncSelectStyles = () => ({
    container: (provided) => ({
      ...provided,
      marginBottom: 30
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: 14,
      color: 'rgba(210, 211, 215, 1)'
    }),
    control: (provided) => ({
      ...provided,
      borderColor: 'rgba(229, 228, 235, 1)',
      fontSize: 14,
      color: 'rgba(52, 64, 85, 1)'
    }),
    menu: (provided) => ({
      ...provided,
      fontSize: 16,
      color: '#344055',
      border: '1px solid rgba(187, 189, 191, 0.3)',
      boxShadow: '-5px 8px 10px 0px rgba(229, 228, 235, 0.4)'
    }),
    option: (provided) => ({
      ...provided,
      color: 'rgba(155, 158, 170, 1)',
      backgroundColor: 'transparent',
      border: 'none',
      padding: 16,
      '&:not(:last-child)': {
        borderBottom: '1px solid rgba(187, 189, 191, 0.3)'
      },
      ':active, &:active, &:hover': {
        backgroundColor: 'rgba(229, 228, 235, 1)'
      }
    })
  });

  const getNoOptionMessage = ({ inputValue }) =>
    inputValue?.length > 0 ? (
      <Trans id="placeholderResultsEmpty" values={{ query: inputValue }} />
    ) : (
      <Trans id="placeholderResultsEmptyValue" />
    );

  const FormPlacesAutocomplete = ({ value, handleChange, placeholder }) => (
    <AsyncSelect
      styles={asyncSelectStyles()}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null
      }}
      noOptionsMessage={(inputValue) => getNoOptionMessage(inputValue)}
      closeMenuOnScroll={true}
      isClearable={false}
      value={value}
      onChange={(option) => handleSelectOption(option, handleChange)}
      placeholder={placeholder}
      loadOptions={loadOptions}
      getOptionLabel={(option) => option.description}
      onFocus={() => handleChange('')}
      aria-label={placeholder}
    />
  );

  return (
    <>
      <div className="geolocalisation">
        <div className="geolocalisation__container">
          <div className={`module ${showMessage ? '--expanded' : ''}`}>
            <HeadingTwo content={<Trans id="geolocalisationModuleTitle" />} />
            <div className="module__label">
              <Description
                isSmall
                content={<Trans id="geolocalisationModuleFrom" />}
              />
            </div>
            <FormPlacesAutocomplete
              value={pickupLocation}
              handleChange={setPickupLocation}
              placeholder={<Trans id="placeholderCityOrZipcodeCollection" />}
            />
            <div className="module__label">
              <Description
                isSmall
                content={<Trans id="geolocalisationModuleTo" />}
              />
            </div>
            <FormPlacesAutocomplete
              value={deliveryLocation}
              handleChange={setDeliveryLocation}
              placeholder={<Trans id="placeholderCityOrZipcodeDestination" />}
            />
            {isCitiesFilledOut && (
              <Button
                onClick={onClickSearch}
                isDisabled={!isCitiesFilledOut}
                button={{
                  version: 'primary-text',
                  label: <Trans id="buttonLabelSearch" />,
                  iconPosition: 'right',
                  icon: 'MdArrowForward',
                  dataLayer: {
                    action: GEOLOC_ACTION,
                    category: GEOLOC_CATEGORY
                  }
                }}
              />
            )}
            {showMessage && (
              <FadeInDiv>
                <div className="module__response">
                  <Description isSmall content={<Trans id={messageId} />} />
                </div>
                <Button
                  button={{
                    version: 'primary',
                    label: (
                      <Trans
                        id={
                          apiAnswer === SUCCEED
                            ? 'buttonLabelQuote'
                            : 'buttonLabelCustomQuote'
                        }
                      />
                    ),
                    externalLink: 'quoting',
                    identifier: 'geo_module'
                  }}
                />
              </FadeInDiv>
            )}
          </div>
          <Map />
        </div>
      </div>
    </>
  );
}
